import type { FC } from 'react';
import React, { useState, useEffect, useRef } from 'react';

import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';

import type { ILoginSocialBarProps } from '../../LoginSocialBar.types';
import LoggedInSocialBar from './components/loggedInSocialBar';
import LoggedOffSocialBar from './components/loggedOffSocialBar';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../bi/events';

import styles from '../style/LoginSocialBar.module.scss';
import semanticClassNames from './LoginSocialBar.semanticClassNames';

const LoginSocialBar: FC<ILoginSocialBarProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    direction,
    isLoggedIn,
    userName,
    loggedInTextState,
    loggedInText,
    customLoggedInText,
    menuItems,
    iconItems,
    currentPrimaryPageHref,
    reportBiOnClick,
    onLogin,
    onLogout,
    onMouseEnter,
    onMouseLeave,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    reportBiOnMenuItemClick,
    translations,
    avatarUri,
    reportBi,
    loggedInAvatarIcon,
    loggedOutAvatarIcon,
    loggedInCustomAvatar,
    loggedOutCustomAvatar,
    enableLoggingInStateInSSR,
    useNewCustomMenuPositioning,
    useNewElementStructure,
    useNestedMenuItems,
    onBlur,
    withNativeThemeOverrides = false,
  } = props;

  const [isHydrated, setIsHydrated] = useState(false);
  const rootElementRef = useRef<HTMLDivElement>(null);

  // only show logged in once hydration finishes for caching
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const onHover = (event: React.MouseEvent<Element, MouseEvent>) => {
    onMouseEnter?.(event);
    if (isLoggedIn && !isMenuOpen) {
      void reportBi(getMembersAreaNavigationEvent({ action: BIAction.Hover }), {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      });
    }
  };

  const openMenu = () => {
    onMenuOpen();
    if (isLoggedIn) {
      void reportBi(
        getMembersAreaNavigationEvent({ action: BIAction.MenuClick }),
        {
          endpoint: MEMBERS_APP_UOU_ENDPOINT,
        },
      );
    }
  };

  const shouldShowLoggedIn = enableLoggingInStateInSSR
    ? isHydrated && isLoggedIn
    : isLoggedIn;

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(
        formatClassNames(semanticClassNames.root, ...customClassNames),
        semanticClassNames.root,
        styles.root,
        className,
      )}
      dir={direction}
      tabIndex={-1}
      onBlur={isLoggedIn && onBlur ? onBlur : undefined}
      ref={rootElementRef}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
    >
      {shouldShowLoggedIn ? (
        <LoggedInSocialBar
          avatarUri={avatarUri}
          isLoggedIn={isLoggedIn}
          userName={userName}
          id={id}
          rootElement={rootElementRef.current}
          useNewCustomMenuPositioning={useNewCustomMenuPositioning}
          loggedInTextState={loggedInTextState}
          loggedInText={loggedInText}
          customLoggedInText={customLoggedInText}
          currentPrimaryPageHref={currentPrimaryPageHref}
          menuItems={menuItems}
          iconItems={iconItems}
          renderIcons={!!iconItems.length}
          isMenuOpen={isMenuOpen}
          onMenuOpen={openMenu}
          reportBiOnMenuItemClick={reportBiOnMenuItemClick}
          onLogout={onLogout}
          onMenuClose={onMenuClose}
          avatarIcon={loggedInAvatarIcon}
          customAvatar={loggedInCustomAvatar}
          translations={translations}
          reportBi={reportBi}
          reportBiOnClick={reportBiOnClick}
          useNewElementStructure={useNewElementStructure}
          useNestedMenuItems={useNestedMenuItems}
          withNativeThemeOverrides={withNativeThemeOverrides}
        />
      ) : (
        <LoggedOffSocialBar
          id={id}
          avatarIcon={loggedOutAvatarIcon}
          customAvatar={loggedOutCustomAvatar}
          handleLogin={onLogin}
          reportBiOnClick={reportBiOnClick}
          translations={translations}
          useNewElementStructure={useNewElementStructure}
        />
      )}
    </div>
  );
};

export default LoginSocialBar;
